import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { loginUrlWithReturn } from '../admin/adminUtils';

import { ReviewStars } from './reviewDisplay';

function Stars({ rating, size = 'md' }) {
  return <ReviewStars rating={rating} size={size} />;
}

function reviewAuthHeaders(token) {
  const t = String(token || '').trim();
  if (!t) return {};
  return {
    Authorization: `Bearer ${t}`,
    'X-Admin-Session': t,
  };
}

export default function ProductReviews({ categorySlug, productId }) {
  const location = useLocation();
  const { isAuthed, isCustomer, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [average, setAverage] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState('');
  const [msg, setMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        category_slug: categorySlug,
        product_id: String(productId),
      });
      if (isCustomer && token) {
        qs.set('eligibility', '1');
      }
      const r = await fetch(`/api/reviews.php?${qs}`, {
        headers: reviewAuthHeaders(token),
      });
      const d = await r.json();
      if (d.success) {
        setReviews(Array.isArray(d.reviews) ? d.reviews : []);
        setAverage(Number(d.average) || 0);
        setCount(Number(d.count) || 0);
        setEligibility(d.eligibility || null);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [categorySlug, productId, isCustomer, token]);

  useEffect(() => {
    load();
  }, [load]);

  const canReview = Boolean(isCustomer && eligibility?.can_review);

  const submit = async (e) => {
    e.preventDefault();
    setMsg('');
    setSubmitting(true);
    try {
      const r = await fetch('/api/reviews.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...reviewAuthHeaders(token),
        },
        body: JSON.stringify({
          session_token: token,
          category_slug: categorySlug,
          product_id: String(productId),
          rating,
          body: body.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setMsg(d.error || 'Could not submit review.');
        return;
      }
      setMsg(d.message || 'Thank you! Your review will appear after we approve it.');
      setBody('');
      setRating(5);
      setEligibility({ purchased: true, already_reviewed: true, can_review: false });
      load();
    } catch {
      setMsg('Could not submit review.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderReviewFormArea = () => {
    if (!isAuthed) {
      return (
        <p className="small text-muted mb-0">
          <Link to={loginUrlWithReturn(location.pathname, location.search)}>Sign in</Link> to leave a review after you purchase this product.
        </p>
      );
    }

    if (!isCustomer) {
      return (
        <p className="small text-muted mb-0">Only customer accounts who bought this product can leave a review.</p>
      );
    }

    if (loading || eligibility === null) {
      return <p className="small text-muted mb-0">Checking if you can review…</p>;
    }

    if (eligibility.already_reviewed) {
      return (
        <p className="small text-muted mb-0">
          You already reviewed this product. Thank you — we will show it after approval.
        </p>
      );
    }

    if (!eligibility.purchased) {
      return (
        <p className="small text-muted mb-0">
          Reviews are only open to customers who bought this item on a paid order.{' '}
          <Link to="/account">View your orders</Link> after checkout.
        </p>
      );
    }

    if (!canReview) {
      return <p className="small text-muted mb-0">You cannot leave a review for this product right now.</p>;
    }

    return (
      <form className="product-reviews-form p-3 border rounded" onSubmit={submit}>
        <h3 className="h6 mb-2">Write a review</h3>
        <p className="small text-muted mb-3">You purchased this product — thank you for sharing your experience.</p>
        <div className="checkout-field mb-3">
          <label className="checkout-field__label" htmlFor="review-rating">
            Rating
          </label>
          <select
            id="review-rating"
            className="form-select checkout-input"
            style={{ maxWidth: 140 }}
            value={rating}
            onChange={(e) => setRating(Number(e.target.value))}
          >
            {[5, 4, 3, 2, 1].map((n) => (
              <option key={n} value={n}>
                {n} star{n === 1 ? '' : 's'}
              </option>
            ))}
          </select>
        </div>
        <div className="checkout-field mb-3">
          <label className="checkout-field__label" htmlFor="review-body">
            Your review
          </label>
          <textarea
            id="review-body"
            className="form-control checkout-input"
            rows={3}
            minLength={10}
            maxLength={2000}
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Tell others about the quality, fit, and how you styled this piece…"
          />
        </div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
          {submitting ? 'Sending…' : 'Submit review'}
        </button>
        {msg ? <p className="small mt-2 mb-0">{msg}</p> : null}
      </form>
    );
  };

  return (
    <section id="product-reviews" className="product-reviews mt-5 pt-4 border-top">
      <h2 className="h5 mb-3">Customer reviews</h2>
      {loading ? (
        <p className="small text-muted">Loading reviews…</p>
      ) : count > 0 ? (
        <p className="mb-3">
          <Stars rating={Math.round(average)} />{' '}
          <span className="fw-semibold">{average.toFixed(1)}</span>
          <span className="text-muted small"> ({count} review{count === 1 ? '' : 's'})</span>
        </p>
      ) : (
        <p className="small text-muted mb-3">No reviews yet — be the first to share your experience.</p>
      )}

      {reviews.length > 0 ? (
        <ul className="list-unstyled product-reviews-list mb-4">
          {reviews.map((rev) => (
            <li key={rev.id} className="product-reviews-item border-bottom pb-3 mb-3">
              <div className="d-flex justify-content-between gap-2 mb-1">
                <strong className="small">{rev.author_name}</strong>
                <span className="small text-muted">
                  {rev.created_at ? new Date(rev.created_at).toLocaleDateString('en-IN') : ''}
                </span>
              </div>
              <Stars rating={rev.rating} size="sm" />
              <p className="small mb-0 mt-2">{rev.body}</p>
            </li>
          ))}
        </ul>
      ) : null}

      {renderReviewFormArea()}
    </section>
  );
}
